import React, { Suspense, lazy } from 'react';
import { Route, Navigate, Outlet } from 'react-router-dom';

// -------------------------------------------------------------
// Core & Layouts
// -------------------------------------------------------------
import AdminLayout from '../layouts/AdminLayout';
import ProtectedRoute from '../Components/ProtectedRoute';

// -------------------------------------------------------------
// Lazy Loaded Pages
// -------------------------------------------------------------
const AdminIndex = lazy(() => import('../Pages/Admin/AdminDashboard'));
const RegistrationsJourneyDetail = lazy(
  () => import('../Pages/Admin/RegistrationsJourneyDetail'),
);
const BatchList = lazy(() => import('../Pages/Admin/Batch/BatchList'));
const BatchAdd = lazy(() => import('../Pages/Admin/Batch/BatchAdd'));
const BatchDetailLayout = lazy(() => import('../Pages/Admin/Batch/BatchDetailLayout'));
const BatchDetail = lazy(() => import('../Pages/Admin/Batch/BatchDetail'));
const BatchTimelinePage = lazy(() => import('../Pages/Admin/Batch/BatchTimelinePage'));
const BatchRegistrationsJourneyPage = lazy(
  () => import('../Pages/Admin/Batch/BatchRegistrationsJourneyPage'),
);
const BatchStatusAcceptancePage = lazy(
  () => import('../Pages/Admin/Batch/BatchStatusAcceptancePage'),
);
const BatchDocumentTemplatesPage = lazy(
  () => import('../Pages/Admin/Batch/BatchDocumentTemplatesPage'),
);
const CertificatesPage = lazy(() => import('../Pages/Admin/Batch/CertificatesPage'));
const CertificateQueuePage = lazy(() => import('../Pages/Admin/Certificate/CertificateQueuePage'));
const ProgramSectionsMaster = lazy(
  () => import('../Pages/Admin/ProgramSections/ProgramSectionsMaster'),
);
const AdminLogin = lazy(() => import('../Pages/Admin/Login'));
const AdminProfile = lazy(() => import('../Pages/Admin/Profile/AdminProfile'));

// -- Publication
const AdminPublicationList = lazy(
  () => import('../Pages/Admin/Publication/PublicationList'),
);
const AdminPublicationDetail = lazy(
  () => import('../Pages/Admin/Publication/PublicationDetail'),
);

// -- Recommendation
const RecommendationPage = lazy(
  () => import('../Pages/Admin/Recommendation/RecommendationPage'),
);

// -- Capacity Building
const CapacityBuilding = lazy(
  () => import('../Pages/Admin/CapacityBuilding/CapacityBuilding'),
);
const CapacityBuildingForm = lazy(
  () => import('../Pages/Admin/CapacityBuilding/CapacityBuildingForm'),
);
const CapacityBuildingDetail = lazy(
  () => import('../Pages/Admin/CapacityBuilding/CapacityBuildingDetail'),
);
const CapacityBuildingAddQuestion = lazy(
  () => import('../Pages/Admin/CapacityBuilding/CapacityBuildingAddQuestion'),
);
const CapacityBuildingTaskDetail = lazy(
  () => import('../Pages/Admin/CapacityBuilding/CapacityBuildingTaskDetail'),
);
const CapacityBuildingTestDetail = lazy(
  () => import('../Pages/Admin/CapacityBuilding/CapacityBuildingTestDetail'),
);

// -- Registrations (Shared with Mentor)
const RegistrationsList = lazy(
  () => import('../Pages/Mentor/Registrations/RegistrationsList'),
);
const RegistrationsDetail = lazy(
  () => import('../Pages/Mentor/Registrations/RegistrationsDetail'),
);

// -- Evaluations
const AdminEvaluationList = lazy(
  () => import('../Pages/Admin/Evaluation/EvaluationList'),
);
const AdminEvaluationDetail = lazy(
  () => import('../Pages/Admin/Evaluation/EvaluationDetail'),
);
const AdminEvaluationAdd = lazy(
  () => import('../Pages/Admin/Evaluation/EvaluationAdd'),
);
const EvaluationHSAdd = lazy(
  () => import('../Pages/Mentor/Evaluation/EvaluationHSAdd'),
);

// -- PAT Evaluations
const AdminPATEvaluationReceivedDetail = lazy(
  () => import('../Pages/Admin/EvaluationPAT/PATEvaluationDetail'),
);
const AdminPATEvaluationGivenDetail = lazy(
  () => import('../Pages/Admin/EvaluationPAT/PATScoreDetail'),
);

// -- Classifications
const ClassificationsHardskills = lazy(
  () => import('../Pages/Admin/Classifications/ClassificationsHardskills'),
);
const ClassificationsAdd = lazy(
  () => import('../Pages/Admin/Classifications/ClassificationsAdd'),
);
const ClassificationsUpdate = lazy(
  () => import('../Pages/Admin/Classifications/ClassificationsUpdate'),
);
const ClassificationsBCF = lazy(
  () => import('../Pages/Admin/Classifications/ClassificationsBCF'),
);

// -- Users & Mentors
const AdminUsersList = lazy(
  () => import('../Pages/Admin/Users/AdminUsersList'),
);
const AdminUsersDetail = lazy(() => import('../Pages/Admin/Users/UsersDetail'));
const AdminUsersUpdateStatus = lazy(
  () => import('../Pages/Admin/Users/UsersUpdateStatus'),
);
const MentorsList = lazy(() => import('../Pages/Admin/Mentors/MentorsList'));
const MentorsDetail = lazy(
  () => import('../Pages/Admin/Mentors/MentorsDetail'),
);
const MentorsAdd = lazy(() => import('../Pages/Admin/Mentors/MentorsAdd'));

// -- Institutions & Universities
const AdminInstitutionsList = lazy(
  () => import('../Pages/Admin/Institutions/InstitutionsList'),
);
const AdminInstitutionsAdd = lazy(
  () => import('../Pages/Admin/Institutions/InstitutionsAdd'),
);
const AdminInstitutionsDetail = lazy(
  () => import('../Pages/Admin/Institutions/InstitutionsDetail'),
);
const AdminInstitutionsEdit = lazy(
  () => import('../Pages/Admin/Institutions/InstitutionsEdit'),
);
const AdminInstitutionsDivisionsAdd = lazy(
  () => import('../Pages/Admin/Institutions/Divisions/DivisionsAdd'),
);
const AdminInstitutionsDivisionsDetail = lazy(
  () => import('../Pages/Admin/Institutions/Divisions/DivisionsDetail'),
);
const AdminInstitutionsDivisionsEdit = lazy(
  () => import('../Pages/Admin/Institutions/Divisions/DivisionsEdit'),
);
const AdminInstitutionsDivisionsMaster = lazy(
  () => import('../Pages/Admin/Institutions/Divisions/DivisionsMaster'),
);
const AdminUniversitiesList = lazy(
  () => import('../Pages/Admin/Universities/UniversitiesList'),
);
const AdminUniversitiesAdd = lazy(
  () => import('../Pages/Admin/Universities/UniversitiesAdd'),
);
const UniversitiesDetail = lazy(
  () => import('../Pages/Admin/Universities/UniversitiesDetail'),
);
const UniversitiesUpdate = lazy(
  () => import('../Pages/Admin/Universities/UniversitiesUpdate'),
);

// -- Attendances
const AdminAttendancesList = lazy(
  () => import('../Pages/Admin/Attendances/AttendancesList'),
);
const AdminAttendancesDetail = lazy(
  () => import('../Pages/Admin/Attendances/AttendancesDetail'),
);
const AdminAttendancesParticipantDetail = lazy(
  () => import('../Pages/Admin/Attendances/AttendancesParticipantDetail'),
);

// -- BUMDes (Approval, Pre-Survey, VECI, Leadership, BMC)
const AdminBumdesList = lazy(() => import('../Pages/Admin/Bumdes/BumdesList'));
const AdminBumdesDetail = lazy(
  () => import('../Pages/Admin/Bumdes/BumdesDetail'),
);
const AdminBumdesApprovalList = lazy(
  () => import('../Pages/Admin/Bumdes/BumdesApprovalList'),
);
const AdminBumdesApprovalDetail = lazy(
  () => import('../Pages/Admin/Bumdes/BumdesApprovalDetail'),
);
const AdminPreSurveyBumdesList = lazy(
  () => import('../Pages/Admin/Bumdes/PreSurvey/PreSurveyList'),
);
const AdminPreSurveyBumdesDetail = lazy(
  () => import('../Pages/Admin/Bumdes/PreSurvey/PreSurveyDetail'),
);
const AdminRedCloudList = lazy(
  () => import('../Pages/Admin/Bumdes/RedCloud/RedCloudAdminList'),
);
const AdminRedCloudDetail = lazy(
  () => import('../Pages/Admin/Bumdes/RedCloud/RedCloudAdminDetail'),
);

// -- BUMDes Survey VECI
const AdminVECIDashboard = lazy(
  () => import('../Pages/Admin/Bumdes/SurveyVECI/VECIDashboard'),
);
const AdminVECIList = lazy(
  () => import('../Pages/Admin/Bumdes/SurveyVECI/VECIList'),
);
const AdminSurveyVECIInput = lazy(
  () => import('../Pages/Admin/Bumdes/SurveyVECI/AdminSurveyVECIInput'),
);
const AdminVECIReview = lazy(
  () => import('../Pages/Admin/Bumdes/SurveyVECI/VECIReview'),
);
const AdminVECIAward = lazy(
  () => import('../Pages/Admin/Bumdes/SurveyVECI/VECIAward'),
);
const AdminVECIRanking = lazy(
  () => import('../Pages/Admin/Bumdes/SurveyVECI/VECIRanking'),
);
const AdminVECIHeatmap = lazy(
  () => import('../Pages/Admin/Bumdes/SurveyVECI/VECIHeatmap'),
);

// -- BUMDes Leadership & BMC
const AdminLeadershipList = lazy(
  () => import('../Pages/Admin/Bumdes/SurveyLeadership/LeadershipList'),
);
const AdminLeadershipReview = lazy(
  () => import('../Pages/Admin/Bumdes/SurveyLeadership/LeadershipReview'),
);
const AdminBMCList = lazy(() => import('../Pages/Admin/Bumdes/BMC/BMCList'));
const AdminBMCReview = lazy(
  () => import('../Pages/Admin/Bumdes/BMC/BMCReview'),
);

const FallbackLoader = () => (
  <div
    className='d-flex justify-content-center align-items-center py-5'
    style={{ minHeight: '50vh' }}
  >
    <div
      className='spinner-border text-primary'
      role='status'
    >
      <span className='visually-hidden'>Loading...</span>
    </div>
  </div>
);

const adminRoutes = (authUser) => (
  <Route path='admin'>
    <Route
      path='login'
      element={
        authUser &&
        authUser?.activeSession?.roleCode === 'ADMIN' ? (
          <Navigate
            to='/admin'
            replace
          />
        ) : (
          <Suspense fallback={<FallbackLoader />}>
            <AdminLogin />
          </Suspense>
        )
      }
    />

    <Route element={<ProtectedRoute allowedRoles={['Admin']} />}>
      <Route element={<AdminLayout />}>
        <Route
          element={
            <Suspense fallback={<FallbackLoader />}>
              <Outlet />
            </Suspense>
          }
        >
          <Route
            index
            element={<AdminIndex />}
          />
          <Route
            path='registrations-journey'
            element={<RegistrationsJourneyDetail />}
          />
          <Route
            path='program-sections'
            element={<ProgramSectionsMaster />}
          />
          <Route path='batch'>
            <Route
              index
              element={<BatchList />}
            />
            <Route
              path='add'
              element={<BatchAdd />}
            />
            {/* Dipecah jadi beberapa halaman (tab) per konteks — dulu satu
                halaman panjang berisi form + 4 section config sekaligus. */}
            <Route
              path=':id'
              element={<BatchDetailLayout />}
            >
              <Route
                index
                element={<BatchDetail />}
              />
              <Route
                path='timeline'
                element={<BatchTimelinePage />}
              />
              <Route
                path='registrations-journey'
                element={<BatchRegistrationsJourneyPage />}
              />
              <Route
                path='status-acceptance'
                element={<BatchStatusAcceptancePage />}
              />
              <Route
                path='document-templates'
                element={<BatchDocumentTemplatesPage />}
              />
              <Route
                path='certificates'
                element={<CertificatesPage />}
              />
            </Route>
          </Route>
          <Route
            path='home'
            element={
              <Navigate
                to='/admin'
                replace
              />
            }
          />

          <Route path='publication'>
            <Route
              index
              element={<AdminPublicationList />}
            />
            <Route
              path=':id'
              element={<AdminPublicationDetail />}
            />
          </Route>

          <Route path='recommendation-letter'>
            <Route
              index
              element={<RecommendationPage />}
            />
          </Route>

          <Route path='capacity-building'>
            <Route
              index
              element={<CapacityBuilding />}
            />
            <Route
              path='add'
              element={<CapacityBuildingForm />}
            />
            <Route path=':id'>
              <Route
                index
                element={<CapacityBuildingDetail />}
              />
              <Route
                path='add-test'
                element={<CapacityBuildingAddQuestion />}
              />
              <Route
                path='task'
                element={<CapacityBuildingTaskDetail />}
              />
              <Route
                path='test/:testId'
                element={<CapacityBuildingTestDetail />}
              />
            </Route>
          </Route>

          <Route path='registrations'>
            <Route
              index
              element={<RegistrationsList />}
            />
            <Route path=':id'>
              <Route
                index
                element={<RegistrationsDetail />}
              />
            </Route>
          </Route>

          <Route
            path='certificates'
            element={<CertificateQueuePage />}
          />

          <Route path='students'>
            <Route
              path='evaluations'
              element={<AdminEvaluationList />}
            />
            <Route path=':id/evaluations'>
              <Route
                index
                element={<AdminEvaluationDetail />}
              />
              <Route
                path='new'
                element={<AdminEvaluationAdd />}
              />
              <Route
                path='new/hard-skill'
                element={<EvaluationHSAdd />}
              />
              <Route
                path='pat/received'
                element={<AdminPATEvaluationReceivedDetail />}
              />
              <Route
                path='pat/given'
                element={<AdminPATEvaluationGivenDetail />}
              />
            </Route>
          </Route>

          {/* KPI Divisi (Hard-Skill) — master_clp_classifications_final_hs */}
          <Route path='classifications'>
            <Route
              index
              element={<ClassificationsHardskills />}
            />
            {/* REST fix: consider replacing these to nested division routes in future */}
            <Route
              path=':divisionInstitutionsId/add'
              element={<ClassificationsAdd />}
            />
            <Route
              path=':divisionInstitutionsId/update'
              element={<ClassificationsUpdate />}
            />
          </Route>

          {/* KPI Batch (BCF) — master_clp_classifications_final_bcf, formula v1 only */}
          <Route path='classifications-bcf'>
            <Route
              index
              element={<ClassificationsBCF />}
            />
          </Route>

          <Route path='users'>
            <Route
              index
              element={<AdminUsersList />}
            />
            <Route
              path='update-status'
              element={<AdminUsersUpdateStatus />}
            />
            <Route
              path=':id'
              element={<AdminUsersDetail />}
            />
          </Route>

          <Route path='mentors'>
            <Route
              index
              element={<MentorsList />}
            />
            <Route
              path='add'
              element={<MentorsAdd />}
            />
            {/* Redundant, but kept for backward compatibility */}
            <Route
              path='users/:usersId'
              element={<MentorsDetail />}
            />
            <Route
              path=':id'
              element={<MentorsDetail />}
            />
          </Route>

          <Route path='institutions'>
            <Route
              index
              element={<AdminInstitutionsList />}
            />
            <Route
              path='add'
              element={<AdminInstitutionsAdd />}
            />
            <Route
              path='divisions'
              element={<AdminInstitutionsDivisionsMaster />}
            />
            <Route
              path=':id'
              element={<AdminInstitutionsDetail />}
            />
            <Route
              path=':id/edit'
              element={<AdminInstitutionsEdit />}
            />
            <Route
              path=':id/add'
              element={<AdminInstitutionsDivisionsAdd />}
            />
            <Route
              path=':institutionsId/division/:divisionInstitutionsId'
              element={<AdminInstitutionsDivisionsDetail />}
            />
            <Route
              path=':institutionsId/division/:divisionInstitutionsId/edit'
              element={<AdminInstitutionsDivisionsEdit />}
            />
          </Route>

          <Route path='universities'>
            <Route
              index
              element={<AdminUniversitiesList />}
            />
            <Route
              path='add'
              element={<AdminUniversitiesAdd />}
            />
            <Route
              path=':id'
              element={<UniversitiesDetail />}
            />
            <Route
              path=':id/update'
              element={<UniversitiesUpdate />}
            />
          </Route>

          <Route path='attendances'>
            <Route
              index
              element={<AdminAttendancesList />}
            />
            <Route
              path=':id'
              element={<AdminAttendancesDetail />}
            />
            <Route
              path='participants/:userId'
              element={<AdminAttendancesParticipantDetail />}
            />
          </Route>

          <Route path='bumdes'>
            <Route
              index
              element={<AdminBumdesList />}
            />
            <Route
              path=':id'
              element={<AdminBumdesDetail />}
            />
            <Route path='approval'>
              <Route
                index
                element={<AdminBumdesApprovalList />}
              />
              <Route
                path=':id'
                element={<AdminBumdesApprovalDetail />}
              />
            </Route>
            <Route path='pre-survey'>
              <Route
                index
                element={<AdminPreSurveyBumdesList />}
              />
              <Route
                path=':id'
                element={<AdminPreSurveyBumdesDetail />}
              />
            </Route>
            <Route path='survey-veci'>
              <Route
                index
                element={<AdminVECIDashboard />}
              />
              <Route
                path='list'
                element={<AdminVECIList />}
              />
              <Route
                path='input'
                element={<AdminSurveyVECIInput />}
              />
              <Route
                path='review/:surveyId'
                element={<AdminVECIReview />}
              />
              <Route
                path='award'
                element={<AdminVECIAward />}
              />
              <Route
                path='ranking'
                element={<AdminVECIRanking />}
              />
              <Route
                path='heatmap'
                element={<AdminVECIHeatmap />}
              />
            </Route>
            <Route path='survey-leadership'>
              <Route
                index
                element={<AdminLeadershipList />}
              />
              <Route
                path=':poId'
                element={<AdminLeadershipReview />}
              />
            </Route>
            <Route path='bmc'>
              <Route
                index
                element={<AdminBMCList />}
              />
              <Route
                path='review/:bmcId'
                element={<AdminBMCReview />}
              />
            </Route>
            <Route path='red-cloud'>
              <Route
                index
                element={<AdminRedCloudList />}
              />
              <Route
                path=':id'
                element={<AdminRedCloudDetail />}
              />
            </Route>
          </Route>

          <Route path='profile'>
            <Route
              index
              element={<AdminProfile />}
            />
          </Route>
        </Route>
      </Route>
    </Route>
  </Route>
);

export default adminRoutes;
